import * as _ from "lodash";
import bootbox from "bootbox";

import * as SmartDrawings from "../../smartDrawings";
import { UiUtils } from "../../common/uiUtils";
import { viewerOptions } from "../../viewer";

/**
 * Functions for searching for elements from the search bar and url search parameters. The search parameters are
 * given in format category,displayname,operator,value,category2,displayname2,...etc
 */
export namespace QuickFilterUtils {

    function searchSuccess(results) {
        UiUtils.notifySuccess({
            message: `The search matched ${results.length} element${results.length > 1 ? "s" : ""}.`,
            duration: 5000
        });
    }

    function searchFail() {
        UiUtils.notifyError({
            message: "The search didn't match any elements.",
            duration: 5000
        });
    }

    export async function selectWithFilterString(paramsStr: string, viewer, zoomToFit: boolean) {
        const params = splitParams(paramsStr);
        if (_.isEmpty(params)) {
            return false;
        }
        return await selectWithFilterArrays(params, viewer, zoomToFit);
    }

    export async function isolateWithFilterString(paramsStr: string, viewer, zoomToFit: boolean) {
        const params = splitParams(paramsStr);
        if (_.isEmpty(params)) {
            return false;
        }
        return await isolateWithFilterArrays(params, viewer, zoomToFit);
    }

    (window as any).isolate = isolateWithFilterString;

    export async function selectWithFilterArrays(params: string[], viewer, zoomToFit: boolean) {
        viewer.clearSelection();
        const ids = await searchIds(params, viewer);
        if (!_.isEmpty(ids)) {
            viewer.select(ids);
            if (zoomToFit) {
                viewer.fitToView(ids);
            }

            searchSuccess(ids);
            return true;
        }
        searchFail();
        return false;
    }

    export async function isolateWithFilterArrays(params: string[], viewer, zoomToFit: boolean) {
        viewer.showAll();
        const ids = await searchIds(params, viewer);
        if (!_.isEmpty(ids)) {
            viewer.isolate(ids);
            if (zoomToFit) {
                viewer.fitToView(ids);
            }

            searchSuccess(ids);
            return true;
        }
        searchFail();
        return false;
    }

    async function searchIds(params: string[], viewer) {
        const searchCategory = params[0];
        const searchProperty = params[1];
        const searchOperator = params[2];
        const searchValue = params[3];

        // Exact match
        let exactMatch = false;
        if (searchOperator === "=") {
            exactMatch = true;
        }

        let notif = null;
        notif = UiUtils.notifySuccess({
            message: "Searching...",
            duration: 0
        });

        const idsPdp = await searchNodes(params, viewer, false, exactMatch);
        const idsExt = await searchExternalData(searchOperator, searchValue, exactMatch, searchCategory, searchProperty);
        const ids = _.union(idsPdp, idsExt);

        UiUtils.dismissNotification(notif);
        return ids;
    }

    export async function searchExternalData(searchOperator: string, searchValue, exactMatch: boolean = false, category: string = null, name: string = null) {
        let ids = [];

        if (viewerOptions.externalData) {
            const data: SmartDrawings.ExternalData = viewerOptions.externalData;

            // Check id mapping exist
            if (!data.paramsToDbIds && !viewerOptions.tools.hasIdMap) {
                const msg = "Couldn't search external data, missing id mapping!";
                console.log(msg);
                UiUtils.notifyWarning(msg);
                return ids;
            }

            if (!exactMatch) {
                searchValue = isNaN(searchValue) ? searchValue.toLowerCase() : searchValue;
            }

            let filterProps;

            if (category !== null) {
                filterProps = _.filter(data.propertyDefinitions, p => p.name === name && p.category === category);
            } 

            const guids = _.keys(data.propertyValues);

            var values;

            for (let guid of guids) {
                values = data.propertyValues[guid];

                for (let value in values) {
                    if (Object.prototype.hasOwnProperty.call(values, value) && (filterProps.length > 0 ? value === filterProps[0].name : true)) {
                        let extValue = values[value];
                        if (!exactMatch) {
                            extValue = extValue != null ? extValue.toLowerCase() : extValue;
                        }

                        let nodeId = undefined;

                        if (compareValue(extValue, searchValue, searchOperator)) {
                            if (data.idProperty != null) {
                                nodeId = data.paramsToDbIds[guid];
                            } else if (viewerOptions.tools.hasIdMap) {
                                nodeId = viewerOptions.idMap.nodeId(null, guid);
                            } else {
                                console.log("Incorrect external data search! Missing id mapping!");
                            }

                            // TODO: External data without id mapping?
                            if (nodeId !== undefined) {
                                ids.push(nodeId);
                            }
                        }
                    }
                }

            }
        }
        
        return ids;
    }

    function compareValue(value1, value2, operator: string): boolean {
        let ret = false;
        switch (operator) {
            case "=":
                if (value1 == value2)
                    ret = true;
                break;
            case ">":
                if (value1 > value2)
                    ret = true;
                break;
            case "<":
                if (value1 < value2)
                    ret = true;
                break;
            case "<=":
                if (value1 <= value2)
                    ret = true;
                break;
            case ">=":
                if (value1 >= value2)
                    ret = true;
                break;
            case "~":
                if (value1?.toString().includes(value2))
                    ret = true;
                break;
            default:
                ret = false;
        }

        return ret;
    }

    function splitParams(paramsStr: string): string[] {
        const params = paramsStr.split(",");
        if (_.isEmpty(params) || params.length % 4 !== 0) {
            bootbox.alert("Incorrect number of selection parameters.");
            return [];
        }
        return params;
    }

    export async function searchNodes(params: string[], viewer, notify = true, exactMatch: boolean = false) {
        const paramChunks = _.chunk(params, 4);
        const searchParams = paramChunks.map(c => ({
            category: c[0].toLowerCase(), name: c[1].toLowerCase(), operator: c[2],
            value: exactMatch ? c[3] : c[3].toLowerCase(), exact: exactMatch
        }));

        let notif = null;
        if (notify) {
            notif = UiUtils.notifySuccess({
                message: "Searching...",
                duration: 0
            });
        }
        const results = await viewer.model.getPropertyDb().executeUserFunction(userFunction, searchParams);
        if (notify) {
            UiUtils.dismissNotification(notif);
        }
        return results;
    }
}

/*
 * Function given to the viewer's web worker to brute force search for elements which fill the given search parameters.
 * This needs to be named userFunction (thanks, Autodesk) else it doesn't work. Also there cannot be another userFunction anywhere
 * in code as js minification will only spare one of them from garbling.
 */
function userFunction(pdb, searchParams) {
    const paramIdsToSearchParams = {};

    pdb.enumAttributes((i, attrDef, attrRaw) => {
        for (let sp of searchParams) {
            if (attrDef.category != null && attrDef.displayName != null) {
                if ((attrDef.category.toLowerCase() === sp.category || sp.category === "*") &&
                    (attrDef.displayName.toLowerCase() === sp.name || sp.name === "*")) {
                    paramIdsToSearchParams[i] = sp;
                    if ((sp.category !== "*" && sp.name !== "*") && Object.keys(paramIdsToSearchParams).length === searchParams.length) {
                        return true;
                    }
                }
            }
        }
    });


    if (Object.keys(paramIdsToSearchParams).length === 0) {
        return null;
    }

    const dbIds = [];
    pdb.enumObjects(dbId => {
        let paramsMatched = 0;
        // For each part, iterate over their properties.
        pdb.enumObjectProperties(dbId, (attrId, valId) => {
            const sp = paramIdsToSearchParams[attrId];
            if (sp != null) {
                const value = pdb.getAttrValue(attrId, valId);
                let compared = value;
                if (!sp.exact) {
                    compared = isNaN(compared) ? value.toLowerCase() : compared;
                }
                switch (sp.operator) {
                    case "=":
                        if (compared == sp.value) {
                            paramsMatched++;
                        }
                        break;
                    case ">":
                        if (compared > sp.value) {
                            paramsMatched++;
                        }
                        break;
                    case "<":
                        if (compared < sp.value) {
                            paramsMatched++;
                        }
                        break;
                    case "<=":
                        if (compared <= sp.value) {
                            paramsMatched++;
                        }
                        break;
                    case ">=":
                        if (compared >= sp.value) {
                            paramsMatched++;
                        }
                        break;
                    case "~":
                        if (compared?.toString().includes(sp.value)) {
                            paramsMatched++;
                        }
                        break;
                }

                if (paramsMatched === searchParams.length) {
                    dbIds.push(dbId);
                    return true;
                }
            }
        });
    });

    return dbIds;
}